import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// CONTEXT
import { ToastContext } from 'context/ToastContext';

// SERVICES
import { createTileTypeLink, validateTileProposal, proposeTile } from 'services/tileService';

// COMPONENTS
import Loader from 'components/loaderComponent';
import CheckboxInput from 'components/CheckboxInput';
import ImageCropModal from 'components/common/ImageCropModal';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const TARGET_WIDTH = 64;
const TARGET_HEIGHT = 64;

const dataURLtoFile = (dataurl, filename) => {
  if (!dataurl) return null;
  const arr = dataurl.split(',');
  if (arr.length < 2) return null;
  const mimeMatch = arr[0].match(/:(.*?);/);
  if (!mimeMatch || mimeMatch.length < 2) return null;
  const mime = mimeMatch[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

const TileForm = () => {
  const { addToast } = useContext(ToastContext);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/admin/tiles/management';
  const proposalId = location.state?.proposalId;
  const userId = location.state?.userId;
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    tileId: null,
    titre: '',
    description: '',
    lien: '',
    facultatif: true,
    requiresVPN: false,
    availableForPresta: false,
    image: null,
    image_url: null,
  });

  const [imageSrcForModal, setImageSrcForModal] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [originalImageFile, setOriginalImageFile] = useState(null);

  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const savedData = localStorage.getItem('tileFormData');
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      setFormData({ ...parsedData, image: parsedData.image_url || null });
    } else {
      setFormData({
        tileId: null,
        titre: '',
        description: '',
        lien: '',
        facultatif: true,
        requiresVPN: false,
        availableForPresta: false,
        image: null,
        image_url: null,
      });
    }
    setIsLoading(false);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    // Empêcher la modification de facultatif
    if (name === 'facultatif') {
      return;
    }
    
    const updatedFormData = {
      ...formData,
      [name]: newValue
    };
    
    setFormData(updatedFormData);
    localStorage.setItem('tileFormData', JSON.stringify(updatedFormData));
  };

  const onSelectFile = (e) => {
    setErrorMessage('');
    setFormData(prev => ({ ...prev, image: null }));
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > MAX_FILE_SIZE) {
        setErrorMessage('Le fichier est trop volumineux. Max: ' + (MAX_FILE_SIZE / 1024 / 1024) + 'MB');
        setImageSrcForModal(null);
        setOriginalImageFile(null);
        e.target.value = null; 
        return;
      }
      if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
        setErrorMessage('Type de fichier non supporté. Uniquement JPG, PNG, GIF, WEBP.');
        setImageSrcForModal(null);
        setOriginalImageFile(null);
        e.target.value = null; 
        return;
      }

      setOriginalImageFile(file); 
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrcForModal(reader.result?.toString() || '');
        setShowCropModal(true);
      });
      reader.readAsDataURL(file);
    } else {
      setImageSrcForModal(null);
      setOriginalImageFile(null);
    }
  };
  
  const handleCropSave = (croppedImageDataUrl) => {
    setFormData(prev => ({ ...prev, image: croppedImageDataUrl }));
    const currentStoredData = JSON.parse(localStorage.getItem('tileFormData') || '{}');
    localStorage.setItem('tileFormData', JSON.stringify({ ...currentStoredData, image: croppedImageDataUrl }));
    setShowCropModal(false); 
    setImageSrcForModal(null);
  };

  const handleCloseModal = () => {
    setShowCropModal(false);
    setImageSrcForModal(null);
    const fileInput = document.getElementById('tileImage');
    if (fileInput) {
        fileInput.value = null;
    }
    setOriginalImageFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const payload = new FormData();
      payload.append('tileId', formData.tileId || '');
      payload.append('titre', formData.titre);
      payload.append('description', formData.description);
      payload.append('lien', formData.lien);
      payload.append('facultatif', formData.facultatif);
      payload.append('requiresVPN', formData.requiresVPN);
      payload.append('availableForPresta', formData.availableForPresta);

      if (formData.image) {
        if (typeof formData.image === 'string' && formData.image.startsWith('data:')) {
          const filename = originalImageFile?.name || `tile-${Date.now()}.png`;
          const imageFile = dataURLtoFile(formData.image, filename);
          if (imageFile) {
            payload.append('image', imageFile);
            payload.append('hasImage', 'true');
          } else {
            payload.append('hasImage', 'false');
            console.warn("Conversion de l'image recadrée en fichier a échoué.");
          }
        } else if (typeof formData.image === 'string' && formData.image.startsWith('http')) {
          payload.append('image_url', formData.image);
          payload.append('hasImage', 'true');
        }
      } else if (formData.image_url) {
        payload.append('image_url', formData.image_url);
        payload.append('hasImage', 'true');
      } else {
        payload.append('hasImage', 'false');
      }

      // Si on vient d'une proposition, on utilise proposeTile
      if (proposalId && userId) {
        const proposalData = {
          proposalId: proposalId,
          title: formData.titre,
          description: formData.description,
          link: formData.lien,
          availableForPresta: formData.availableForPresta,
          requiresVPN: formData.requiresVPN,
        };
        await proposeTile(userId, proposalData);
      }
      
      const tileTypeLink = await createTileTypeLink(payload);

      if (proposalId && tileTypeLink.status === 200) {
        await validateTileProposal(proposalId, userId, tileTypeLink.data.tileId);
      }

      if (proposalId && userId) {
        addToast('Succès', 'La proposition a été mise à jour et validée avec succès.', null, null, 'success');
      } else {
        addToast('Succès', 'La tuile a été créée avec succès.', null, null, 'success');
      }

      localStorage.removeItem('tileFormData');
      navigate(from);
    } catch (error) {
      console.error('Erreur lors de la création/modification de la tuile:', error);
      setErrorMessage(error.message || 'Une erreur est survenue lors de la sauvegarde.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    localStorage.removeItem('tileFormData');
    setErrorMessage('');
    setImageSrcForModal(null);
    setOriginalImageFile(null);
    setFormData({
      tileId: null, titre: '', description: '', lien: '',
      facultatif: true, requiresVPN: false, availableForPresta: false, image: null, image_url: null
    });
    const fileInput = document.getElementById('tileImage');
    if (fileInput) {
      fileInput.value = null;
    }
    navigate(from);
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: null }));
    const currentStoredData = JSON.parse(localStorage.getItem('tileFormData') || '{}');
    localStorage.setItem('tileFormData', JSON.stringify({ ...currentStoredData, image: null }));
    const fileInput = document.getElementById('tileImage');
    if (fileInput) {
        fileInput.value = null;
    }
    setOriginalImageFile(null);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="max-w-2xl mx-auto p-4 bg-blanc dark:bg-dark-secondary rounded-lg shadow-dropping-light dark:shadow-dropping-dark hover:shadow-hover-light dark:hover:shadow-hover-dark transition-shadow">
      <h1 className="text-2xl font-bold mb-6">Création/Modification de tuile</h1>
      
      <form autoComplete="off" onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="titre" className="block text-sm font-medium mb-2 dark:text-gray-200">
            Titre <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="titre"
            name="titre"
            value={formData.titre}
            onChange={handleInputChange}
            className="w-full p-2 rounded-lg dark:bg-dark shadow-dropping-light dark:shadow-dropping-dark hover:shadow-hover-light dark:hover:shadow-hover-dark transition-shadow focus:outline-none focus:ring-2 focus:ring-orange focus:ring-opacity-50"
            placeholder="Ex: Trombino"
            required
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium mb-2 dark:text-gray-200">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            className="w-full p-2 rounded-lg dark:bg-dark shadow-dropping-light dark:shadow-dropping-dark hover:shadow-hover-light dark:hover:shadow-hover-dark transition-shadow focus:outline-none focus:ring-2 focus:ring-orange focus:ring-opacity-50"
            rows="3"
            placeholder="Ex: Localisez facilement vos collègues & salles de réunion"
          />
        </div>

        <div>
          <label htmlFor="lien" className="block text-sm font-medium mb-2 dark:text-gray-200">
            Lien <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            id="lien"
            name="lien"
            value={formData.lien}
            onChange={handleInputChange}
            className="w-full p-2 rounded-lg dark:bg-dark shadow-dropping-light dark:shadow-dropping-dark hover:shadow-hover-light dark:hover:shadow-hover-dark transition-shadow focus:outline-none focus:ring-2 focus:ring-orange focus:ring-opacity-50"
            placeholder="Ex: https://trombino.altitudeinfra.fr/"
            required
          />
        </div>

        <div>
          <label htmlFor="tileImage" className="block text-sm font-medium mb-2 dark:text-gray-200">
            Image de la tuile (Optionnel, {TARGET_WIDTH}x{TARGET_HEIGHT}px, fond transparent, max {MAX_FILE_SIZE / 1024 / 1024}MB)
          </label>
          
          <div className="flex items-center space-x-3">
            <label 
              htmlFor="tileImage" 
              className="cursor-pointer px-4 py-2 text-sm text-white bg-orange rounded-lg hover:bg-framboise dark:bg-orange-600 dark:hover:bg-orange-700 transition-colors whitespace-nowrap"
            >
              Choisir un fichier
            </label>
            <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {originalImageFile ? originalImageFile.name : 'Aucun fichier sélectionné.'}
            </span>
          </div>

          <input
            type="file"
            id="tileImage"
            name="tileImage"
            accept="image/png, image/jpeg, image/gif, image/webp"
            onChange={onSelectFile}
            className="hidden"
          />
          {errorMessage && <p className="text-red-500 text-sm mt-1">{errorMessage}</p>}
          {formData.image && (
            <div className="mt-2 flex items-center space-x-2">
              <img 
                src={formData.image} 
                alt="Prévisualisation recadrée" 
                className="border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-700"
                style={{ width: `${TARGET_WIDTH}px`, height: `${TARGET_HEIGHT}px`, objectFit: 'contain' }}
              />
              <button 
                type="button" 
                onClick={removeImage}
                className="text-red-500 hover:text-red-700 text-xs"
              >
                Supprimer l'image
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-4">
          <CheckboxInput
            id="facultatif"
            name="facultatif"
            checked={true}
            disabled={true}
            onChange={() => {}}
            label="Facultatif (Obligatoire)"
          />

          <CheckboxInput
            id="requiresVPN"
            name="requiresVPN"
            checked={formData.requiresVPN}
            onChange={handleInputChange}
            label="Nécessite un VPN"
          />

          <CheckboxInput
            id="availableForPresta"
            name="availableForPresta"
            checked={formData.availableForPresta}
            onChange={handleInputChange}
            label="Disponible pour les prestataires"
          />
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-white bg-orange rounded-lg hover:bg-framboise dark:bg-orange-600 dark:hover:bg-orange-700"
          >
            Enregistrer
          </button>
        </div>
      </form>
      
      <ImageCropModal 
        imageSrcInput={imageSrcForModal}
        show={showCropModal}
        onClose={handleCloseModal}
        onCropSave={handleCropSave}
        targetWidth={TARGET_WIDTH}
        targetHeight={TARGET_HEIGHT}
        initialImageName={originalImageFile?.name}
        imageType={originalImageFile?.type}
      />
    </div>
  );
};

export default TileForm;